// Riverside Residence help fixtures — shared by the dev-only project preview
// harness and the Design-project help articles. Product figures and article
// values read the same constants, so a fixture edit invalidates both together.
//
// The hazard values mirror api/app/services/hazard_provider.py's complete NC
// development report. They are deterministic teaching data, not a live lookup.
import type { HazardOverrides, HazardReportData } from "../../state/hazardOverrides";
import type { ProjectSetup } from "../../state/projectSetup";
import type { StoredSite } from "../../state/projectSite";
import type { Project } from "../../types/project";

export const PROJECT_HELP_PROJECT: Project = {
  id: "project-help-demo",
  name: "Riverside Residence",
  address: "123 Main St, Charlotte, NC 28202",
  status: "draft",
  pipelineStage: 1,
  pipelineStatus: "completed",
  profileName: "residential_engineered_wood_asd_default_v1",
  profileId: "residential_engineered_wood_asd_default_v1",
  createdAt: "2026-07-01T12:00:00.000Z",
  updatedAt: "2026-07-01T12:00:00.000Z",
  createdBy: { id: "demo-user", name: "Jordan Engineer" },
  assignedTo: null,
  inputReadiness: { filled: 17, required: 17 },
  openedInViewerAt: null,
  revisionCount: 0,
  description: "Two-story residence with an attached garage.",
  jurisdiction: "Charlotte, NC",
  hazardReportRef: "hazard-help-demo",
  profileRef: "residential_engineered_wood_asd_default_v1",
  policyRef: "",
  dxfStem: "riverside-residence",
  notes: [],
};

export const PROJECT_HELP_SITE: StoredSite = {
  placeId: "help-riverside-place",
  formattedAddress: PROJECT_HELP_PROJECT.address,
  lat: 35.2271,
  lng: -80.8431,
  manual: false,
  confirmed: true,
};

export const PROJECT_HELP_HAZARD: HazardReportData = {
  site: {
    address: PROJECT_HELP_PROJECT.address,
    lat: PROJECT_HELP_SITE.lat!,
    lon: PROJECT_HELP_SITE.lng!,
    elevation: 800,
    soilClass: "D",
    riskCategory: "II",
  },
  wind: {
    designSpeed: 115,
    speeds: [
      { period: "10-yr", mph: 58 },
      { period: "25-yr", mph: 68 },
      { period: "50-yr", mph: 78 },
      { period: "100-yr", mph: 90 },
      { period: "300-yr", mph: 105 },
      { period: "700-yr", mph: 115 },
    ],
  },
  snow: { pg: 15, mri20: 10, w2: 0.3 },
  seismic: {
    Ss: 0.258,
    S1: 0.085,
    SMS: 0.344,
    SM1: 0.164,
    SDS: 0.229,
    SD1: 0.109,
    TL: 12,
    sdc: "B",
  },
  ice: { thickness: 0.25, temperature: 15, gustSpeed: 40 },
  rain: { intensity15min: 5.2, intensity60min: 3.1 },
  flood: { zone: "X", bfe: 0, datum: "NAVD88" },
  tornado: { check: "Exempt (Risk Category II)" },
  tsunami: { check: "Not in a Tsunami Design Zone" },
  failed: [],
};

export const PROJECT_HELP_HAZARD_SIGNATURE = JSON.stringify({
  lat: PROJECT_HELP_SITE.lat,
  lng: PROJECT_HELP_SITE.lng,
  risk: "II",
  soil: "D",
  edition: "ASCE 7-22",
});

export const PROJECT_HELP_HAZARD_RECORD: HazardOverrides = {
  removed: false,
  manualMode: false,
  values: {},
  governingWindPeriod: null,
  manual: {},
  generated: true,
  asceStandard: "ASCE 7-22",
  riskCategory: "II",
  soilClass: "D",
  report: PROJECT_HELP_HAZARD,
  reportSignature: PROJECT_HELP_HAZARD_SIGNATURE,
};

export const PROJECT_HELP_SETUP_EMPTY: ProjectSetup = {
  floorPlanUploaded: false,
  floorPlanName: null,
  geometryDetected: false,
  hazardSkipped: false,
  manualWind_mph: null,
  siteConfirmed: false,
  hazardConfirmed: false,
  soilConfirmed: false,
  foundationConfirmed: false,
  profileConfirmed: false,
  pipelineRunId: null,
};

export const PROJECT_HELP_SETUP_CONFIRMED: ProjectSetup = {
  ...PROJECT_HELP_SETUP_EMPTY,
  siteConfirmed: true,
  hazardConfirmed: true,
  soilConfirmed: true,
  foundationConfirmed: true,
  profileConfirmed: true,
};

export const PROJECT_HELP_SETUP_UPLOADED: ProjectSetup = {
  ...PROJECT_HELP_SETUP_CONFIRMED,
  floorPlanUploaded: true,
  floorPlanName: "riverside-baseplan.dxf",
  pipelineRunId: "run-help-demo",
};

export const PROJECT_HELP_PIPELINE_READY = {
  runId: "run-help-demo",
  status: "awaiting_approval",
  currentStep: "setup_bundle",
  currentPhase: "setup",
  startedAt: "2026-07-01T11:58:42.000Z",
  stages: [{ stepKey: "setup_bundle", status: "succeeded" }],
  errorMessage: null,
} as const;

export const PROJECT_HELP_PIPELINE_RUNNING = {
  runId: "run-help-demo",
  status: "running",
  currentStep: "setup_bundle",
  currentPhase: "setup",
  startedAt: "2026-07-01T11:59:42.000Z",
  stages: [{ stepKey: "setup_bundle", status: "running" }],
  errorMessage: null,
} as const;
